import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, MessageCircle, Sparkles, Calendar, AlertCircle, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_LINK = "https://wa.me/27714375240";

const ELIGIBILITY_ITEMS = [
  'Open to residents of South Africa aged 18 or older.',
  'Employees, affiliates, and immediate family members of K&K Medical and Dental are not eligible.',
  'Existing patients of K&K Medical and Dental are eligible for exclusive rewards in addition to the standard prizes.',
];

const ENTRY_RULES = [
  'All entries must be made exclusively through the official WhatsApp chatbot.',
  'To enter, participants must send the word "promo" to the chatbot (+27 71 437 5240) and follow the instructions provided.',
  'All required fields must be completed accurately on the chatbot; incomplete or incorrect entries will not be considered for the draw.',
  'No purchase or consultation necessary to enter or win.',
  'Limit of 1 entry per person. Multiple entries beyond this limit will be disqualified.',
  'Entries must be received by the 3rd Monday of each month.',
];

const PRIZE_DETAILS = [
  'Draws will be done on the last Thursday of each month.',
  'Description of the prizes will be on the website monthly — www.kkmedical.co.za/prizes',
  'Existing patients may qualify for additional or enhanced rewards, as determined by K&K Medical and Dental.',
  'There will be a period of 30 days in which collection of the prize can be done.',
  'The patient will be phoned and informed of winning a prize/s and given details on the collection address.',
  'The collection address will not necessarily be the locations of K&K Medical Dental Clinics.',
  'Prizes are non-transferable and cannot be exchanged for cash or other alternatives.',
  'Prizes will NOT be sent to winners via courier/mail. Collection is the only option.',
  'K&K Medical and Dental reserves the right to substitute a prize of equal or greater value if necessary.',
];

const TermItem = ({ text }) => (
  <li className="flex items-start gap-3 text-slate-700 leading-relaxed">
    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0 mt-2.5" />
    <span>{text}</span>
  </li>
);

export const Prizes = () => {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/prizes`)
      .then(res => setPrizes(res.data || []))
      .catch(() => setPrizes([]))
      .finally(() => setLoading(false));
  }, []);

  const monthName = new Date().toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen" data-testid="prizes-page">
      <SEO
        title="Monthly Prize Draw"
        description="Up to R2,000 in prizes every month at K&K Medical and Dental. See this month's prize lineup, enter via WhatsApp, and read the official terms and conditions."
        keywords="K&K prize draw, monthly prizes Umzinto, free prizes Scottburgh, win prizes KZN, healthcare giveaway"
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 text-white text-sm font-semibold mb-6 backdrop-blur-sm">
            <Trophy className="w-4 h-4" />
            Monthly Prize Draw
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4">
            Up to <span className="text-amber-200">R2,000</span> in Prizes
          </h1>
          <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl mx-auto mb-8">
            Every month we give away amazing prizes to our community. Here's what's up for grabs this month.
          </p>
          <Button
            asChild
            className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all"
            data-testid="prizes-enter-btn"
          >
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Enter via WhatsApp
            </a>
          </Button>
        </div>
      </section>

      {/* This month's prizes */}
      <section className="py-16 lg:py-24 bg-white" data-testid="this-month-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-amber-600 mb-3 inline-flex items-center gap-2 justify-center">
              <Calendar className="w-4 h-4" /> {monthName}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              This Month's Prizes
            </h2>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="rounded-3xl bg-slate-100 animate-pulse h-72" />
              ))}
            </div>
          ) : prizes.length === 0 ? (
            <Card className="border-0 shadow-lg rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50">
              <CardContent className="p-10 text-center">
                <Gift className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">Prizes coming soon!</h3>
                <p className="text-slate-600 max-w-xl mx-auto">
                  This month's prize lineup is being finalised. Check back shortly, or enter early via WhatsApp so you don't miss out.
                </p>
                <Button
                  asChild
                  className="mt-6 bg-amber-500 hover:bg-amber-600 text-white rounded-full px-6"
                  data-testid="empty-state-enter-btn"
                >
                  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" /> Enter Now
                  </a>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {prizes.map((prize, idx) => (
                <Card
                  key={prize.id}
                  className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 rounded-3xl overflow-hidden bg-white"
                  data-testid={`prize-card-${idx}`}
                >
                  {prize.image_url ? (
                    <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                      <img src={prize.image_url} alt={prize.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                      <Sparkles className="w-12 h-12 text-amber-500" />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{prize.title}</h3>
                    {prize.description && (
                      <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">{prize.description}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How to enter */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="how-to-enter-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-8 text-white text-center">
              <Trophy className="w-12 h-12 mx-auto mb-3" />
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">How to Enter</h2>
              <p className="text-white/90">Free entry via WhatsApp — takes under a minute</p>
            </div>
            <CardContent className="p-8 space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center flex-shrink-0">1</div>
                <p className="text-slate-700 leading-relaxed">
                  Send the word <span className="font-bold text-amber-700">"promo"</span> to our official WhatsApp chatbot on <span className="font-bold">+27 71 437 5240</span>.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center flex-shrink-0">2</div>
                <p className="text-slate-700 leading-relaxed">
                  Follow the chatbot's prompts and complete every required field accurately.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center flex-shrink-0">3</div>
                <p className="text-slate-700 leading-relaxed">
                  That's it — entries must be in by the <span className="font-semibold">3rd Monday of the month</span>. Draws happen on the <span className="font-semibold">last Thursday of the month</span>.
                </p>
              </div>
              <Button
                asChild
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full py-6 text-lg shadow-lg mt-2"
                data-testid="how-to-enter-btn"
              >
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" /> Enter Now via WhatsApp
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Terms & Conditions */}
      <section id="terms" className="py-16 lg:py-24 bg-white scroll-mt-24" data-testid="terms-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold uppercase tracking-widest mb-4">
              <AlertCircle className="w-4 h-4" /> Official Rules
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Prize Giveaway Terms &amp; Conditions
            </h2>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">1. Eligibility</h3>
              <ul className="space-y-3">
                {ELIGIBILITY_ITEMS.map((t) => <TermItem key={t} text={t} />)}
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">2. Entry Rules and How to Enter</h3>
              <ul className="space-y-3">
                {ENTRY_RULES.map((t) => <TermItem key={t} text={t} />)}
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">3. Prize Details</h3>
              <ul className="space-y-3">
                {PRIZE_DETAILS.map((t) => <TermItem key={t} text={t} />)}
              </ul>
            </div>
          </div>

          <div className="mt-12 p-6 rounded-2xl bg-slate-50 border border-slate-200 text-center">
            <p className="text-slate-600 text-sm">
              Questions about the giveaway? <Link to="/contact" className="text-amber-600 hover:text-amber-700 font-medium underline">Contact us</Link> or message us directly on WhatsApp.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
